import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './SignOutConfirmDialog.css';

/**
 * Accessible sign-out confirmation; portals to document.body for correct stacking.
 */
const SignOutConfirmDialog = ({ open, onCancel, onConfirm }) => {
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCancel();
    };

    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onCancel]);

  useEffect(() => {
    if (!open || !panelRef.current) return;
    const cancelBtn = panelRef.current.querySelector('[data-sign-out-cancel]');
    cancelBtn?.focus();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="sign-out-dialog-root">
      <button
        type="button"
        className="sign-out-dialog-backdrop"
        aria-label="Dismiss"
        onClick={onCancel}
      />
      <div
        ref={panelRef}
        className="sign-out-dialog-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sign-out-dialog-title"
        aria-describedby="sign-out-dialog-desc"
      >
        <div className="sign-out-dialog__body">
          <p className="sign-out-dialog__eyebrow">Session</p>
          <h2 className="sign-out-dialog__title" id="sign-out-dialog-title">
            Sign out of your account?
          </h2>
          <p className="sign-out-dialog__desc" id="sign-out-dialog-desc">
            Any unsaved work in other tabs may be lost. You will need to sign in again to access
            your company dashboard.
          </p>
        </div>
        <footer className="sign-out-dialog__actions">
          <button
            type="button"
            className="sign-out-dialog__btn sign-out-dialog__btn--secondary"
            data-sign-out-cancel
            onClick={onCancel}
          >
            Stay signed in
          </button>
          <button
            type="button"
            className="sign-out-dialog__btn sign-out-dialog__btn--danger"
            onClick={onConfirm}
          >
            Sign out
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default SignOutConfirmDialog;
